import { useState } from "react";
import { ViewHeader } from "./PortalShared";
import { Calculator, Home } from "lucide-react";
import { Input } from "./ui/input";

function fmt(n) {
  if (!n && n !== 0) return "$0";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n);
}

const BUYER_ITEMS = [
  { key: "title_insurance",     label: "Title Insurance (Buyer's Policy)",    pct: 0.5,   min: 500 },
  { key: "escrow_fee",          label: "Settlement Fee",             pct: 0.1,   min: 400 },
  { key: "loan_origination",    label: "Loan Origination Fee (1 pt)",         pct: 1.0,   min: 0,  loanBased: true },
  { key: "appraisal",           label: "Appraisal Fee",                       flat: 650 },
  { key: "home_inspection",     label: "Home Inspection",                     flat: 450 },
  { key: "survey",              label: "Survey",                              flat: 550 },
  { key: "recording_fees",      label: "Recording Fees",                      flat: 250 },
  { key: "prepaid_interest",    label: "Prepaid Interest (15 days)",          pct: 0.04,  loanBased: true },
  { key: "homeowners_insurance",label: "Homeowners Insurance (1 yr)",         flat: 1400 },
  { key: "property_tax_reserve",label: "Property Tax Reserve (3 mo)",         pct: 0.275  },
];

const SELLER_ITEMS = [
  { key: "listing_agent",       label: "Listing Agent Commission (3%)",       pct: 3.0 },
  { key: "buyers_agent",        label: "Buyer's Agent Commission (3%)",       pct: 3.0 },
  { key: "title_search",        label: "Title Search / Commitment",           flat: 400 },
  { key: "owner_title_policy",  label: "Owner's Title Policy",                pct: 0.4,   min: 400 },
  { key: "escrow_fee_seller",   label: "Settlement Fee",             pct: 0.1,   min: 400 },
  { key: "recording_release",   label: "Mortgage Release / Payoff Fees",      flat: 150 },
  { key: "transfer_tax",        label: "Transfer / Documentary Stamps",       pct: 0.7 },
  { key: "hoa_fees",            label: "HOA Transfer & Estoppel",             flat: 350 },
  { key: "attorney_fees",       label: "Attorney / Closing Agent Fees",       flat: 800 },
];

function calcItem(item, salePrice, loanAmt) {
  if (item.flat) return item.flat;
  const base = item.loanBased ? loanAmt : salePrice;
  const calc = (base * item.pct) / 100;
  return item.min ? Math.max(calc, item.min) : calc;
}

export default function ClosingCostEstimator() {
  const [form, setForm] = useState({ sale_price: "", loan_amount: "", property_address: "" });
  const [overrides, setOverrides] = useState({});
  const [result, setResult] = useState(null);
  const [activeTab, setActiveTab] = useState("buyer");

  const calculate = () => {
    const price = parseFloat(form.sale_price) || 0;
    const loan = parseFloat(form.loan_amount) || 0;
    if (!price) { return; }

    const buyerBreakdown = BUYER_ITEMS.map(item => ({
      ...item,
      amount: overrides[item.key] !== undefined ? parseFloat(overrides[item.key]) : calcItem(item, price, loan)
    }));
    const sellerBreakdown = SELLER_ITEMS.map(item => ({
      ...item,
      amount: overrides[item.key] !== undefined ? parseFloat(overrides[item.key]) : calcItem(item, price, loan)
    }));

    const buyerTotal = buyerBreakdown.reduce((s, i) => s + i.amount, 0);
    const sellerTotal = sellerBreakdown.reduce((s, i) => s + i.amount, 0);

    setResult({ price, loan, buyerBreakdown, sellerBreakdown, buyerTotal, sellerTotal });
  };

  return (
    <div>
      <ViewHeader title="Closing Cost Estimator" subtitle="Estimate buyer and seller closing costs for any transaction" />
      <div className="grid grid-cols-3 gap-6">
        <div className="bg-[#0A0A0A] border border-white/[0.06] rounded-xl p-5 space-y-4">
          <p className="text-[10px] text-white/30 uppercase tracking-wider font-semibold">Transaction Details</p>
          <div>
            <label className="text-[10px] text-white/40 block mb-1">Property Address (optional)</label>
            <Input value={form.property_address} onChange={e => setForm(f=>({...f,property_address:e.target.value}))} className="bg-white/[0.03] border-white/[0.08] text-white h-8 text-sm" />
          </div>
          <div>
            <label className="text-[10px] text-white/40 block mb-1">Sale Price</label>
            <Input type="number" value={form.sale_price} onChange={e => setForm(f=>({...f,sale_price:e.target.value}))} className="bg-white/[0.03] border-white/[0.08] text-white h-8 text-sm" placeholder="0" />
          </div>
          <div>
            <label className="text-[10px] text-white/40 block mb-1">Loan Amount</label>
            <Input type="number" value={form.loan_amount} onChange={e => setForm(f=>({...f,loan_amount:e.target.value}))} className="bg-white/[0.03] border-white/[0.08] text-white h-8 text-sm" placeholder="0" />
          </div>
          <button onClick={calculate}
            data-testid="calculate-closing-btn"
            className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm rounded transition-all flex items-center justify-center gap-2">
            <Calculator className="w-4 h-4" /> Calculate Costs
          </button>
        </div>
        <div className="col-span-2">
          {result ? (
            <div>
              <div className="flex gap-3 mb-4">
                <div className="bg-[#0A0A0A] border border-emerald-500/20 rounded-xl p-4 flex-1 text-center">
                  <p className="text-white/40 text-xs">Buyer's Est. Costs</p>
                  <p className="text-emerald-400 text-2xl font-bold">{fmt(result.buyerTotal)}</p>
                  <p className="text-white/30 text-[10px]">{((result.buyerTotal / result.price) * 100).toFixed(1)}% of price</p>
                </div>
                <div className="bg-[#0A0A0A] border border-[#D4AF37]/20 rounded-xl p-4 flex-1 text-center">
                  <p className="text-white/40 text-xs">Seller's Est. Costs</p>
                  <p className="text-[#D4AF37] text-2xl font-bold">{fmt(result.sellerTotal)}</p>
                  <p className="text-white/30 text-[10px]">{((result.sellerTotal / result.price) * 100).toFixed(1)}% of price</p>
                </div>
              </div>
              <div className="flex gap-2 mb-4">
                {["buyer", "seller"].map(tab => (
                  <button key={tab} onClick={() => setActiveTab(tab)}
                    className={`text-xs px-4 py-2 border rounded transition-all capitalize ${activeTab === tab ? 'bg-[#D4AF37]/10 border-[#D4AF37]/30 text-[#D4AF37]' : 'bg-white/[0.02] border-white/[0.06] text-white/40 hover:text-white'}`}>
                    {tab === "buyer" ? "Buyer" : "Seller"}'s Costs
                  </button>
                ))}
              </div>
              <div className="bg-[#0A0A0A] border border-white/[0.06] rounded-xl p-4 space-y-2">
                {(activeTab === "buyer" ? result.buyerBreakdown : result.sellerBreakdown).map(item => (
                  <div key={item.key} className="flex items-center justify-between border-b border-white/[0.04] pb-2">
                    <span className="text-white/50 text-sm">{item.label}</span>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        value={overrides[item.key] !== undefined ? overrides[item.key] : Math.round(item.amount)}
                        onChange={e => setOverrides(p => ({ ...p, [item.key]: e.target.value }))}
                        onBlur={calculate}
                        className="bg-white/[0.03] border-white/[0.06] text-white h-6 w-24 text-xs text-right"
                      />
                    </div>
                  </div>
                ))}
                <div className="flex items-center justify-between pt-1">
                  <span className="text-white font-bold text-sm">Total</span>
                  <span className={`text-lg font-bold ${activeTab === "buyer" ? "text-emerald-400" : "text-[#D4AF37]"}`}>
                    {fmt(activeTab === "buyer" ? result.buyerTotal : result.sellerTotal)}
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full py-20 bg-white/[0.01] border border-white/[0.05] rounded-xl">
              <Home className="w-12 h-12 text-emerald-400/30 mb-4" />
              <p className="text-white/30 text-sm">Enter sale price to estimate closing costs</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
